<template>
  <VSpinner v-if="!product" class="offset-top-16 offset-bottom-17" />
  <article v-else>
    <VForm :form="form" @submit="btnNext(true)">
      <div :id="isFinalize ? 'PRODUCT_ORDER_FINALIZE' : 'PRODUCT_ORDER_SELECTION'" class="container-fixed">
        <!-- #53 -->
        <VProgress v-if="flags.progressBarEnabled" class="offset-top-2 offset-bottom-2" :steps="steps" active="substore.progress_selection" />

        <VOrderDescription :product="product" property-suffix="Selection" />

        <VSection v-if="product.texts.productRequirementsDetails" cluster label="substore.requirements">
          <p v-html="product.texts.productRequirementsDetails"><!-- #30 --></p>
        </VSection>

        <VInput v-model="modifiedFixedLineNumber" name="fln" :label="labelFLN" :hint="$t('substore.Web2T_NotValidFixedLineNumber')" :placeholder="textFLN" required/>
      </div>

      <div v-if="!hidePrice" class="container-fluid grayed offset-top-1">
        <div class="container-fixed">
          <section class="offset-top-1" style="padding-bottom: 6px">
            <VPriceDetails v-bind="product" />
          </section>
        </div>
      </div>

      <div class="container-fixed">
        <div v-if="flags.allowReservationEmail" class="offset-top-2">
          <p>
            {{ $t('substore.oform_please_enter_email_of_customer_to_send_reservation_confirmation') }}
            <!-- #D527 -->
          </p>
          <!-- #S875 + #S876 + #D526 -->
          <VInput
            v-model="flags.customerEmail"
            autocomplete="email"
            name="customerEmail"
            :label="$t('substore.oform_customers_email_address')"
            :hint="$t('substore.oform_please_enter_valid_email')"
            :placeholder="$t('substore.oform_customers_email_address')"
            required
            label-as-required
          />
        </div>

        <p
          v-if="product.texts.dataTransferDenialText && (flags.isFinalizationAllowed || flags.isCompleteOrderAllowed)"
          class="offset-top-1"
          v-html="product.texts.dataTransferDenialText"
        ></p>

        <a
          v-if="product.texts.dataTransferDenialLinkText && (flags.isFinalizationAllowed || flags.isCompleteOrderAllowed)"
          href="summary"
          :class="disabledLink"
          class="offset-top-1"
          @click.prevent="btnNext(false)"
          v-html="product.texts.dataTransferDenialLinkText"
        ></a>

        <p
          v-if="product.texts.redirectHint && (flags.isFinalizationAllowed || flags.isCompleteOrderAllowed)"
          class="offset-top-1"
          v-html="product.texts.redirectHint"
        >
          <!-- #72 -->
        </p>

        <!-- TODO: Order Form fields: 271 - 278, 160 - 163 -->

        <!-- TODO: ACE fields: 251, 252 -->

        <div class="row">
          <div v-if="isBusy" class="col-l-4 col-s-12">
            <VSpinner class="pull-left offset-top-2" />
          </div>
          <section class="offset-top-2 pull-right offset-right-1 button-bar">
            <!-- #73 -->
            <button type="button" class="btn btn-clean" :disabled="isBusy" @click="btnAbort">
              {{ $t('substore.abort') }}
            </button>

            <!-- #74 -->
            <button
              v-if="
                !flags.subscriptionBeginIsRunning && !product.canNotSucceed && ((flags.isFinalization && flags.isFinalizationAllowed) || !flags.isFinalization)
              "
              type="submit"
              class="offset-left-1 btn btn-default btn-brand"
              :disabled="isBusy"
            >
              {{ $t('substore.' + btnLabel) }}
            </button>
            <span v-else-if="!flags.subscriptionBeginIsRunning">
              {{ $t('substore.' + (product.canNotSucceed ? 'msg_order_currently_not_possible' : 'msg_order_forbidden')) }}
            </span>
            <span v-else>
              {{ $t('substore.msg_order_in_process') }}
            </span>
          </section>
        </div>
      </div>
    </VForm>

    <div class="container-fixed offset-top-6">
      <VFootnotes :footnotes="footnotes" />
    </div>
  </article>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import Form from '@/components/Form/Form';
import Input from '@/components/Form/Input';
import { checkSupportedFixedLineNumbers, checkValidations, formattedFixedLineNumber, initFields, isValidEmail, isValidFixedLineNumber } from '@/utils/form';
import Proxy from '@/proxies/SubstoreOrderSelectionProxy';
import ProductDetailsProxy from '@/proxies/SubstoreProductDetailsProxy';
import Transformer from '@/transformers/ProductOrderSelectionTransformer';
import Footnotes from '@/utils/Footnotes';
import TitleMixin from '@/mixins/title';
import OrderStepsMixin from '@/mixins/orderSteps';
import VSpinner from '@/components/Spinner';
import VSection from '@/components/Section';
import emitter from '@/plugins/emitter';
import subscriptionBegin from '@/views/Substore/Product/subscriptionBegin';
import { ERROR, PRODUCT_CATALOGUE, PRODUCT_ORDER_FINALIZE, PRODUCT_ORDER_SELECTION, STOCK } from '@route-types';

export default {
  name: 'ProductOrderSelection',

  components: {
    VSpinner,
    VSection,
    VProgress: () => import('@/components/Progress'),
    VOrderDescription: () => import('@/components/Substore/OrderDescription'),
    VPriceDetails: () => import('@/components/Substore/PriceDetails'),
    VForm: Form,
    VInput: Input,
    VFootnotes: () => import('@/components/UI/Footnotes'),
  },

  mixins: [TitleMixin, OrderStepsMixin],

  props: {
    subscriptionTemplateId: {
      type: String,
      required: false,
      default: null,
    },

    originalSubscriptionTemplateId: {
      type: String,
      required: false,
      default: null,
    },

    fixedLineNumber: {
      type: String,
      required: false,
      default: null,
    },
  },

  data() {
    return {
      product: null,
      footnotes: null,
      modifiedFixedLineNumber: this.fixedLineNumber,
      form: initFields(
        {
          name: 'fln',
          validators: [isValidFixedLineNumber, checkSupportedFixedLineNumbers],
          filters: [formattedFixedLineNumber],
          isHidden: true,
        },
        {
          name: 'customerEmail',
          validators: [isValidEmail],
          isHidden: true,
        }
      ),
      optin: false,
      isFinalize: this.$route.name === PRODUCT_ORDER_FINALIZE,
      isBusy: false,
      flags: {
        orderSelectionSkipped: null,
        progressBarEnabled: null,
        isOnlyReservationAllowed: null,
        isPurchaseRedirectRequired: null,
        thirdPartyAuthentication: null,
        isFinalizationAllowed: null,
        isCompleteOrderAllowed: null,
        subscriptionBookedByExtern: null,
        subscriptionBeginIsRunning: null,
        isFinalization: null,
        isResumingSubscription: null,
        customerEmail: null,
        allowReservationEmail: null,
      },
    };
  },

  computed: {
    ...mapState('route', ['meta']),

    ...mapGetters('system', ['hasAccessCatalogue', 'hasAccessStockOverview']),

    ...mapState('system', ['isDeeplink', 'deeplinkSubscriptionTemplateId']),
    labelFLN() {
      if (this.product.texts.fixedLineNumberLabel) {
        return this.product.texts.fixedLineNumberLabel;
      }
      return this.$t('substore.Web2T_FixedLineNumberLabel');
    },

    textFLN() {
      if (this.product.texts.fixedLineNumberText) {
        return this.product.texts.fixedLineNumberText;
      }
      return this.$t('substore.Web2T_FixedLineNumberText');
    },
    disabledLink() {
      return this.isBusy ? 'disabled' : '';
    },

    btnLabel() {
      return this.flags.isResumingSubscription
        ? 'continue_order_process'
        : this.flags.isFinalization
        ? 'complete_order'
        : this.flags.isOnlyReservationAllowed
        ? 'reserve'
        : 'next';
    },

    hidePrice() {
      return this.isFinalize || !(this.product.price || this.product.texts.regularPriceOverrideText);
    },
  },

  async created() {
    this.isBusy = false;
    if (this.isDeeplink && this.deeplinkSubscriptionTemplateId && !this.subscriptionTemplateId) {
      if (this.isFinalize) {
        this.$router.replace({
          name: PRODUCT_ORDER_FINALIZE,
          params: { subscriptionTemplateId: this.deeplinkSubscriptionTemplateId },
        });
        return;
      }
      this.$router.replace({
        name: PRODUCT_ORDER_SELECTION,
        params: { subscriptionTemplateId: this.deeplinkSubscriptionTemplateId },
      });
      return;
    }

    if (this.isDeeplink && this.subscriptionTemplateId && !this.isFinalize) {
      this.subscriptionTemplateId = this.deeplinkSubscriptionTemplateId;
      const detailsResponse = await new ProductDetailsProxy().find(this.subscriptionTemplateId);
      if (detailsResponse.head.fault !== null) throw new Error(detailsResponse.head.fault);
    }

    const response = await new Proxy().find(this.subscriptionTemplateId);
    if (response.head.fault !== null) throw new Error(response.head.fault);

    // save orderCallId to session so it may be retrieved in subscriptionBegin.js if we resume the order flow by deep-link to the account step (/catalogue/<subTempId/account/finalize)
    sessionStorage.setItem('orderCallId', response.data.orderCallId);

    Object.keys(this.flags).forEach(key => {
      this.flags[key] = response.data[key];
    });

    if (this.isDeeplink) {
      this.$store.dispatch('system/resetDeeplink');
    }

    if (response.data.orderSelectionSkipped) {
      await this.btnNext(true);
      return;
    }

    this.footnotes = new Footnotes();
    this.product = Transformer.fetch(response.data.product, this.footnotes);
    if (this.product.inputType === 'FLN') this.form.fln.isHidden = false;
    if (this.flags.allowReservationEmail) this.form.customerEmail.isHidden = false;
    emitter.emit('setBannerPicture', this.product.images.headerImageSelectionResourcePath);
    emitter.emit('setBannerHeadline', this.product.texts.headerTitleSelection);
    emitter.emit('setBannerSubheadline', this.product.texts.headerSubTitleSelection);

    if (this.flags.isOnlyReservationAllowed) {
      this.$webtrekk.viewPageWithPayload('bestellreservierung', {
        page_type: 'basket',
        shop_product_sku: this.subscriptionTemplateId,
        shop_product_name: this.product.texts.productName,
      });
    } else {
      this.$webtrekk.viewPageWithPayload('warenkorb', {
        page_type: 'basket',
        shop_product_sku: this.subscriptionTemplateId,
        shop_product_status: 'view',
        shop_product_name: this.product.texts.productName,
      });
    }
  },

  methods: {
    btnAbort() {
      if (this.flags.subscriptionBookedByExtern) {
        // = DescElemToBeSetToFinalize
        if (this.hasAccessStockOverview) {
          this.$router.push({ name: STOCK });
        } else {
          this.$router.push({ name: ERROR, params: { code: 'CANCEL_TARGET_DIALOGUE_NOT_ALLOWED' } });
        }
      } else if (this.hasAccessCatalogue) {
        this.$router.push({ name: PRODUCT_CATALOGUE });
      } else {
        this.$router.push({ name: ERROR, params: { code: 'CANCEL_TARGET_DIALOGUE_NOT_ALLOWED' } });
      }
    },

    async btnNext(dataTransferAccepted) {
      if (this.isBusy) {
        return;
      }
      this.isBusy = true;
      if (dataTransferAccepted === false) {
        this.form.customerEmail.isHidden = true;
      }
      const params = {
        subscriptionTemplateId: this.subscriptionTemplateId,
        dataTransferAccepted,
        requestedParameter: this.modifiedFixedLineNumber,
        createCustomerOrderForm: false, // TODO pl: $scope.confirmation.createCustomerOrderForm
        customerSignatureObtained: false, // TODO pl: $scope.confirmation.customerSignatureObtained
        customerEmail: this.flags.customerEmail, // TODO pl: $scope.confirmation.customerEmail
      };

      if (this.flags.orderSelectionSkipped || checkValidations(this.form)) {
        await subscriptionBegin(params, this.originalSubscriptionTemplateId);
      } else {
        this.isBusy = false;
      }
    },
  },
};
</script>

<style scoped>
.button-bar > span {
  position: relative;
  top: 1px;
  display: inline-block;
}

.disabled {
  color: gray;
  pointer-events: none;
  cursor: not-allowed;
}

form {
  max-width: max-content;
  margin: auto;
}
</style>
